import React from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";

import { green } from "@mui/material/colors";

import { Box, Chip, Drawer as MuiDrawer, ListItemButton } from "@mui/material";

import { ReactComponent as Logo } from "../../vendor/logo.svg";
import { SidebarItemsType } from "../../types/sidebar";
import Footer from "./SidebarFooter";
import SidebarNav from "./SidebarNav";
import useAuth from "../../hooks/useAuth";
import useIsAssociationSuspended from "../../hooks/useIsAssociationSuspended";
import { useEffectivePermissions } from "../../hooks/usePermission";

const Drawer = styled(MuiDrawer)`
  border-right: 0;

  > div {
    border-right: 0;
  }
`;

const Brand = styled(ListItemButton)<{
  component?: React.ReactNode;
  to?: string;
}>`
  font-size: ${(props) => props.theme.typography.h5.fontSize};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  color: ${(props) => props.theme.sidebar.header.color};
  background-color: ${(props) => props.theme.sidebar.header.background};
  font-family: ${(props) => props.theme.typography.fontFamily};
  min-height: 56px;
  padding-left: ${(props) => props.theme.spacing(6)};
  padding-right: ${(props) => props.theme.spacing(6)};
  justify-content: center;
  cursor: pointer;
  flex-grow: 0;

  ${(props) => props.theme.breakpoints.up("sm")} {
    min-height: 64px;
  }

  &:hover {
    background-color: ${(props) => props.theme.sidebar.header.background};
  }
`;

const BrandIcon = styled(Logo)`
  margin-right: ${(props) => props.theme.spacing(2)};
  color: ${(props) => props.theme.sidebar.header.brand.color};
  fill: ${(props) => props.theme.sidebar.header.brand.color};
  width: 32px;
  height: 32px;
`;

const BrandChip = styled(Chip)`
  background-color: ${green[700]};
  border-radius: 5px;
  color: ${(props) => props.theme.palette.common.white};
  font-size: 55%;
  height: 18px;
  margin-left: 2px;
  margin-top: -16px;
  padding: 3px 0;

  span {
    padding-left: ${(props) => props.theme.spacing(1.375)};
    padding-right: ${(props) => props.theme.spacing(1.375)};
  }
`;

export type SidebarProps = {
  PaperProps: {
    style: {
      width: number;
    };
  };
  variant?: "permanent" | "persistent" | "temporary";
  open?: boolean;
  onClose?: () => void;
  items: {
    title: string;
    pages: SidebarItemsType[];
  }[];
  showFooter?: boolean;
};

const Sidebar: React.FC<SidebarProps> = ({
  items,
  showFooter = true,
  onClose,
  ...rest
}) => {
  const { associationSubscription, currentUser } = useAuth();

  // Get active association ID
  const activeAssociationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  // Fetch effective permissions for current user in active association
  const { permissions } = useEffectivePermissions(activeAssociationId);

  const trialExpired =
    associationSubscription?.trialEnd &&
    new Date(associationSubscription.trialEnd) < new Date();

  // A frozen association is locked out of everything, so grey the whole nav out
  // rather than leaving links that would only fail further in.
  const suspended = useIsAssociationSuspended();

  const locked = trialExpired || suspended;

  const itemsWithDisabled = items.map((section) => ({
    ...section,
    pages: section.pages.map((item) => ({
      ...item,
      disabled: locked || item.disabled,
      children: item.children?.map((child) => ({
        ...child,
        disabled: locked || child.disabled,
      })),
    })),
  }));

  return (
    <Drawer variant="permanent" {...rest}>
      <Brand component={NavLink as any} to="/">
        <BrandIcon /> <Box ml={1}>Tiehoitokunta</Box>
      </Brand>
      <SidebarNav
        items={itemsWithDisabled}
        onClose={onClose}
        permissions={permissions}
      />
      {!!showFooter && <Footer />}
    </Drawer>
  );
};

export default Sidebar;
